import { useState } from 'react';
import { wildcardPhase, type HypeMember } from '@movienight/shared';
import { BulbFrame } from './BulbFrame';
import { PitchCallCard } from './PitchCallCard';
import { ScreeningCard } from './ScreeningCard';
import { SeatSheet } from './SeatSheet';
import { useBoard } from './useBoard';
import './board.css';

/**
 * The Now Showing board (DESIGN.md §7).
 *
 * A vertical stack of wide cards, soonest first, capped at 1180px — past that
 * the synopsis line gets too long to scan. It used to be a horizontal
 * snap-scroller of poster cards, and that was the wrong shape: a rail is a
 * browse UI, and this is three or four things in order, usually one. Rows that
 * scroll sideways also hide their own contents, which is a strange answer for
 * the surface whose entire job is "what is playing and when".
 *
 * ## The empty state is the point
 *
 * "The most common state of this site, statistically, is *nothing currently
 * scheduled*, and that state deserves as much design attention as a screening
 * night" (§16a). So there is no spinner-shaped apology here and no grey "no
 * results" box: an empty board is a marquee that reads USKORO, the bouncers
 * have something to say about the quiet — and the corkboard card is right
 * there underneath, because a week with nothing on it is exactly the week
 * somebody should be pitching something.
 *
 * The loading state renders nothing at all rather than a skeleton. The night
 * sky behind it is already a finished page, and a flash of placeholder cards on
 * a board that is usually empty is a worse first frame than the sky.
 */

/**
 * ## The Pitch Room is somewhere else entirely
 *
 * It used to be a modal that `Board` owned and rendered in its return. But this
 * component returns **early** for its loading and error states, and those
 * branches did not carry the modal with them — so a single failed board refetch
 * (a blip, a restart, a slow 401) unmounted the room out from under whoever was
 * typing in it, losing the card they were halfway through. Intermittent,
 * unattributable, and nothing to do with the room itself.
 *
 * It is now a page at `#/predlozi` (`lib/route.ts`), which deletes that class of
 * bug rather than fixing it: nothing here renders it, the links to it are plain
 * `<a href>`, and on that page the board is not mounted at all.
 */

/** Cycled per page load, not per second — a joke that moves is a distraction. */
const QUIET_LINES = [
  '„Nema ništa večeras.” — Armane, koji je i dalje na vratima.',
  '„Ja sam rekao petak. Niko me nije slušao.” — Versace',
  '„Neko mora da predloži nešto. Ne gledaj mene.” — Armane',
];

export function Board({ me }: { me: HypeMember }) {
  const { board, now, engulfed, revealing, tapHype } = useBoard(me);
  /**
   * Which card's booking sheet is open, by id rather than by object.
   *
   * The board refetches under it — somebody else books a seat, the hype list
   * reconciles — and holding the screening itself would pin the sheet to a
   * snapshot that quietly goes stale. Looking it up each render also gives the
   * right behaviour for free when a night ends while the sheet is open: it
   * leaves the board, the lookup misses, the sheet closes.
   */
  const [bookingId, setBookingId] = useState<string | null>(null);

  if (board.status === 'loading') return null;

  if (board.status === 'error') {
    return (
      <>
        <BulbFrame className="night-card">
          <div className="night-card__inner">
            <h2>Raspored se ne učitava</h2>
            <p>{board.message}</p>
          </div>
        </BulbFrame>
        {/* A board that cannot load is not a reason to lose the way in to the
            corkboard — the room is a different fetch and usually fine. */}
        <div className="board board--quiet">
          <PitchCallCard voting={false} />
        </div>
      </>
    );
  }

  /** A slot still taking votes — not one that has closed and is being counted. */
  const voting = board.screenings.some((s) => wildcardPhase(s, now) === 'voting');

  if (board.screenings.length === 0) {
    const line = QUIET_LINES[Math.floor(Math.random() * QUIET_LINES.length)]!;
    return (
      <>
        <BulbFrame className="night-card">
          <div className="night-card__inner board-empty">
            <h2 className="board-empty__title">USKORO</h2>
            <p className="board-empty__sub">Ništa još nije zakazano.</p>
            <p className="board-empty__line selectable">{line}</p>
          </div>
        </BulbFrame>
        <div className="board board--quiet">
          <PitchCallCard voting={voting} />
        </div>
      </>
    );
  }

  const booking = board.screenings.find((s) => s.id === bookingId) ?? null;

  return (
    <>
      <section className="board" aria-label="Na programu">
        {board.screenings.map((screening) => (
          <ScreeningCard
            key={screening.id}
            screening={screening}
            now={now}
            meId={me.id}
            burning={engulfed.has(screening.id)}
            revealing={revealing.get(screening.id) ?? null}
            onTapHype={() => tapHype(screening.id)}
            onBook={() => setBookingId(screening.id)}
          />
        ))}

        {/* Always last, always different. See PitchCallCard. */}
        <PitchCallCard voting={voting} />
      </section>

      {booking && (
        <SeatSheet screening={booking} me={me} onClose={() => setBookingId(null)} />
      )}
    </>
  );
}
